import {Component, Input, OnInit} from '@angular/core';
import {Step} from '../../../stepper/step/step';
import {StepComponent, StepData} from '../../../stepper/step/step.component';
import {StepperService} from '../../../stepper/stepper.service';

import {ColDef, GridOptions, GridReadyEvent, ICellRendererParams} from 'ag-grid-community';
import {TranslateService} from '@ngx-translate/core';
import {MydocmaPrompt, PromptDialogComponent} from '@mydocma/component-library/src/lib/prompt-dialog';
import {MatDialog} from '@angular/material/dialog';
import {DatePipe} from '@angular/common';
import {CustomTooltipComponent} from '../custom-tooltip.component';
import {ImportConfig} from "../../configs/import.config";


interface ErrorArray {
    headerName?: string;
    rowIndex?: number;
    colIndex?: number;
}

@Component({
    selector: 'mydocma-import-step-file-preview',
    templateUrl: './step-file-preview.component.html',
    styleUrls: ['./step-file-preview.component.scss'],
    providers: [DatePipe]
})
export class StepFilePreviewComponent implements OnInit, StepComponent {

    @Input()
    public step: Step;

    @Input()
    public fieldsToImportConfig: ImportConfig;

    public gridApi = null;
    public rowIndexError: ErrorArray[] = [];
    public gridOptions: GridOptions = {
        columnDefs: [],
        rowData: [],
        rowSelection: 'single',
        singleClickEdit: true,
        suppressCellSelection: true,
        suppressColumnVirtualisation: true,
        suppressDragLeaveHidesColumns: true,
        defaultColDef: {
            resizable: true,
            sortable: true,
            headerValueGetter: this.localizeHeader.bind(this),
        },
        context: {componentParent: this},
    };
    public frameworkComponents: { customTooltip: typeof CustomTooltipComponent; };
    private data = null;

    private currentMappingData = null;

    constructor(
        private readonly stepperService: StepperService,
        private readonly translateService: TranslateService,
        private readonly matDialog: MatDialog,
        private datePipe: DatePipe
    ) {
    }

    public ngOnInit() {
        this.frameworkComponents = {customTooltip: CustomTooltipComponent};
        this.translateService.onLangChange.subscribe(() => {

            if (this.gridApi?.refreshHeader) {
                this.gridApi.refreshHeader();
            }
        });
    }

    public async enter() {
        const stepDataMapping = this.stepperService.getStepByName('fileDataMapping').data;
        if (JSON.stringify(stepDataMapping) !== JSON.stringify(this.currentMappingData)) {
            this.currentMappingData = stepDataMapping;
            this.data = await this.filterMappedData();
            this.renderTableGrid(this.data);
        }

        this.stepperService.setStepData(this.step, {
            error: null,
            data: this.prepareValidData(this.data),
            valid: true
        });

    }

    public async validate(): Promise<StepData> {
        const stepData: StepData = {
            error: null,
            data: this.prepareValidData(this.data),
            valid: true
        };

        if (this.rowIndexError.length) {
            const modalValue = await this.promptDiscardChanges();

            if (!modalValue) {
                stepData.allowNext = false;
            }
        }

        return stepData;
    }

    public async nextStep() {
        await this.stepperService.nextStep();
    }

    public async previousStep() {
        await this.stepperService.prevStep();
    }

    public onGridReady($event: GridReadyEvent) {
        this.gridApi = $event.api;
        this.autoSizeAll(false);
    }

    public toolTipValueGetter(params) {
        if (params.value === 'invalid') {


            return params.rowData;
        }
    }

    private localizeHeader(parameters: ICellRendererParams): string {
        const headerIdentifier = parameters.colDef.headerName;
        const foundedConfig = this.fieldsToImportConfig.fields.find((configToFind) => configToFind.name === headerIdentifier);
        if (foundedConfig?.required) {
            return `${this.translateService.instant(foundedConfig?.label)}*`;
        }
        if (foundedConfig) {
            return this.translateService.instant(foundedConfig?.label);
        }
    }

    private excelDateToJSDate(date) {
        return new Date(Math.round((date - 25569) * 86400 * 1000));
    }

    private async filterMappedData(): Promise<any[][]> {
        let num = 0;
        const stepSelectFile = this.stepperService.getStepByName('selectFile');
        const stepDataMapping = this.stepperService.getStepByName('fileDataMapping').data;
        const data = await this.checkIfHasSource(stepSelectFile.data?.data);
        const newArray = [];
        for (const [key, value] of Object.entries(stepDataMapping)) {
            const valueToFind = data[0].find(item => item === value);
            if (valueToFind) {

                const tableBodyIndex = data[0].indexOf(value);
                const tableHeaderIndex = data[0].indexOf(key);
                data.forEach((sheetRow, i) => {
                    const label = this.fieldsToImportConfig.fields.find(configToFind => configToFind.name === key);
                    if (i === 0 && num === 0) {
                        newArray.push([' ']);
                    }
                    if (i !== 0 && num === 0) {
                        newArray.push(['valid']);
                    }
                    if ((!sheetRow[tableBodyIndex] && label.required)
                    ) {
                        newArray[i][0] = ' ';
                        if (i !== 0) {
                            this.rowIndexError.push({
                                headerName: label.label,
                                rowIndex: i - 1,
                                colIndex: tableHeaderIndex + 1
                            });
                            newArray[i][0] = 'invalid';
                        }

                    }
                    if ((label.type !== typeof sheetRow[tableBodyIndex] && label?.type !== 'Date')) {
                        newArray[i][0] = ' ';
                        if (i !== 0) {
                            this.rowIndexError.push({
                                headerName: label.label,
                                rowIndex: i - 1,
                                colIndex: tableHeaderIndex + 1
                            });
                            newArray[i][0] = 'invalid';
                        }
                    }
                    if (label?.type === 'Date' && !this.excelDateToJSDate(sheetRow[tableBodyIndex]).getDate()) {
                        if (i !== 0) {
                            this.rowIndexError.push({
                                headerName: label.label,
                                rowIndex: i - 1,
                                colIndex: tableHeaderIndex + 1
                            });
                            newArray[i][0] = 'invalid';
                        }
                    }

                    if ((label.source || label.getSource) && this.rowIndexError.find((errorToFind) => {
                        return errorToFind.rowIndex === i - 1 && errorToFind.colIndex === tableBodyIndex + 1;
                    })) {
                        newArray[i][0] = 'invalid';
                    }
                    if (i === 0) {
                        newArray[i].push((sheetRow[tableHeaderIndex]));
                    }
                    if (i !== 0) {
                        if (label.type === 'Date' && this.excelDateToJSDate(sheetRow[tableBodyIndex]).getDate()) {
                            newArray[i].push(this.datePipe.transform(this.excelDateToJSDate(sheetRow[tableBodyIndex]), 'yyyy-MM-dd'));
                        } else {
                            newArray[i].push((sheetRow[tableBodyIndex]));
                        }
                    }

                });
            }
            num++;
        }
        return newArray;
    }

    private checkIfHasSource(filteredData): Promise<any[][]> {
        const data = JSON.parse(JSON.stringify(filteredData));
        for (const headerItems of data[0]) {
            const index = data[0].indexOf(headerItems);
            const foundedField = this.fieldsToImportConfig.fields.find((config) => {
                return config.name.toLowerCase() === headerItems.toLowerCase() && (config?.getSource || config?.source);
            });
            if (foundedField) {
                data.forEach(async (filteredSheet, ind) => {
                    const filteredDataIndex = data.indexOf(filteredSheet);
                    const valueToChange = ((foundedField?.source)
                        ? foundedField.source : await foundedField?.getSource())
                        .find((sourceValue => sourceValue.id === filteredSheet[index]));
                    if (filteredDataIndex !== 0 && valueToChange) {
                        filteredSheet[index] = valueToChange.text;
                    }
                    if (filteredDataIndex !== 0 && !valueToChange) {
                        this.rowIndexError.push({
                            headerName: foundedField.label,
                            colIndex: index + 1,
                            rowIndex: ind - 1
                        });
                    }
                });
            }
        }
        return data;
    }

    private autoSizeAll(skipHeader) {
        const allColumnIds = [];
        this.gridOptions.columnApi.getAllColumns().forEach((column) => {
            allColumnIds.push(column.getColId());
        });

        this.gridOptions.columnApi.autoSizeColumns(allColumnIds, skipHeader);
        this.gridOptions.columnApi.autoSizeAllColumns(false);
    }

    private renderTableGrid(filteredData: any[][]): void {
        const newRow = [];
        const newCol: ColDef[] = [];
        filteredData.forEach(((sheetRowArray, rowIndex) => {
            if (rowIndex === 0) {
                let fieldName = 0;
                sheetRowArray.forEach((headerRow, columnIndex) => {
                    let column = {
                        headerName: headerRow, field: (fieldName++).toString(),
                        tooltipValueGetter: (params) => {
                            const isCellValid = this.rowIndexError.filter((errorArray) => {
                                return errorArray.rowIndex === params.rowIndex;
                            }).map((value => this.translateService.instant(value.headerName))).join(', ');


                            if (params.value === 'invalid') {
                                return `fields contains errors in columns: ${isCellValid}`;
                            }

                        },
                        cellRenderer: (params) => {
                            if (params.value === 'invalid') {
                                return '<span><i class="material-icons" style="color: red">error_outline</i>';
                            }
                            if (params.value === 'valid') {
                                return '<span><i class="material-icons" style="color: #509c50">done</i>';
                            }
                            return params.value;
                        },
                        cellStyle: (params) => {
                            const isCellValid = this.rowIndexError.find((errorArray) => {
                                return errorArray.rowIndex === params.rowIndex
                                    && errorArray.colIndex === columnIndex;
                            });

                            if (isCellValid) {
                                return {border: '1px solid #ff7376'};
                            }
                        }
                    };
                    if (columnIndex === 0) {
                        column = Object.assign(column, {
                            resizable: false,
                            maxWidth: 60,
                            pinned: true
                        });
                    }

                    newCol.push(column);
                });

                return;
            }

            newRow.push(Object.assign({}, sheetRowArray));
        }));
        if (this.gridApi) {
            this.gridApi.setColumnDefs(newCol);
            this.gridApi.setRowData(newRow);
            this.gridApi.redrawRows();

            return;
        }
        this.gridOptions.rowData = newRow;
        this.gridOptions.columnDefs = newCol;
    }

    private async promptDiscardChanges(): Promise<boolean> {
        const dialogData: MydocmaPrompt = {
            title: await this.translateService.get('IE.step-file-data-preview.dialog-preview.title').toPromise(),
            content: await this.translateService.get('IE.step-file-data-preview.dialog-preview.content').toPromise(),
            yes: await this.translateService.get('IE.step-file-data-preview.dialog-preview.next').toPromise(),
            no: await this.translateService.get('IE.step-file-data-preview.dialog-preview.close').toPromise(),
        };
        const dialogRef = this.matDialog.open(PromptDialogComponent, {data: dialogData});

        return await dialogRef.afterClosed().toPromise();
    }

    private prepareValidData(sheetData: any[][]) {
        return sheetData.filter((rowToDelete) => {
            return !rowToDelete.includes('invalid');
        }).map((row) => {
            return row.slice(1);
        });
    }
}
